import { Edit05Icon, PlusIcon, Tool02Icon } from '@langchain/untitled-ui-icons';
import { Modal, ModalDialog } from '@mui/joy';

import { dequal } from 'dequal';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import SaveConfirmationModal from '@/components/SaveConfirmationModal';
import { EditorModalHeader } from '@/components/SchemaEditor/components/EditorModalHeader';
import { TruncatedTextWithTooltip } from '@/components/TruncatedTextWithTooltip.tsx';
import { Button, GroupedTabs, Tooltip } from '@/design-system';
import {
  BuiltinTool,
  OpenAIBuiltinTool,
  PromptTool,
  builtinToolsForModelId,
  emptyOpenAITool,
  hasBuiltinTools,
  isOpenAITool,
} from '@/utils/ai-tools';
import { cn } from '@/utils/tailwind';

import { CHAT_MODEL_MAPPING } from '../../Playground.constants';
import { StructuredPromptSchema } from '../../components/manifest/serialized/prompts/ManifestStructuredPrompt';
import { getToolDisplayName } from '../../components/manifest/serialized/utils/toolsUtils';
import { BuiltinToolEditor } from './BuiltinToolEditor';
import { CustomToolEditor } from './CustomToolEditor';
import {
  convertStructuredPromptSchemaToTool,
  convertToolToStructuredPromptSchema,
} from './convertTools.utils';

const MAX_HEIGHT = 640;

const TOOL_TYPES = {
  CUSTOM: 'custom',
  BUILTIN: 'builtin',
} as const;

type ToolType = (typeof TOOL_TYPES)[keyof typeof TOOL_TYPES];

export const ToolModal = ({
  isOpen,
  onClose,
  tools,
  setTools,
  modelId,
  setIsStreaming,
}: {
  isOpen: boolean;
  onClose: () => void;
  tools: PromptTool[];
  setTools: (tools: PromptTool[]) => void;
  modelId: string; // model id, eg. ChatOpenAI
  setIsStreaming: (isStreaming: boolean) => void;
}) => {
  const defaultToolIndex = tools.length ? 0 : undefined;
  const [activeToolIndex, setActiveToolIndex] = useState<number | undefined>(
    defaultToolIndex
  );

  const isAddingNew = activeToolIndex === undefined;

  const currentTool = !isAddingNew ? tools[activeToolIndex] : emptyOpenAITool();

  const value = useMemo(() => {
    if (isOpenAITool(currentTool)) {
      return convertToolToStructuredPromptSchema(
        currentTool.function,
        !isAddingNew
      );
    }
    return currentTool;
  }, [currentTool, isAddingNew]);

  const [stagedValue, setStagedValue] = useState(value);

  const builtinsForProvider = builtinToolsForModelId(modelId);

  const [toolType, setToolType] = useState<ToolType>(() => {
    // Initialize based on the initial tool structure
    if (
      builtinsForProvider.map((b) => b.tool.type).includes(currentTool.type)
    ) {
      return TOOL_TYPES.BUILTIN;
    }
    return TOOL_TYPES.CUSTOM;
  });

  const isCustomTool = useMemo(
    () =>
      (isAddingNew && toolType === TOOL_TYPES.CUSTOM) ||
      (!isAddingNew &&
        !builtinsForProvider
          .map((b) => b.tool.type)
          .includes(currentTool.type)),
    [toolType, isAddingNew, currentTool.type, builtinsForProvider]
  );

  const hasUnsavedChanges = useMemo(() => {
    return !dequal(value, stagedValue);
  }, [value, stagedValue]);

  const getSaveTooltipText = () => {
    if (isOpenAITool(stagedValue) && !stagedValue.title)
      return 'Enter a name for the tool';
    if (!hasUnsavedChanges) return 'Make changes to save';
    return undefined;
  };

  const getToolNameError = () => {
    if (
      tools.some((tool) => {
        if (isOpenAITool(tool)) {
          return tool.function.name === stagedValue.title;
        }
        return false;
      }) &&
      // if the staged value is not the same as the original value, then assume this is a different tool
      stagedValue.title !== value.title
    ) {
      return 'A tool with this name already exists';
    }
    return undefined;
  };

  // reset staged value to original value when the tool changes
  useEffect(() => {
    setStagedValue(value);
  }, [value]);

  const listScrollRef = useRef<HTMLDivElement>(null);

  const handleChange = (tool: PromptTool) => {
    if (isAddingNew) {
      setTools([tool, ...tools]);
      setActiveToolIndex(0);
    } else {
      const updatedTools = [...tools];
      updatedTools[activeToolIndex] = tool;
      setTools(updatedTools);
    }
  };

  const handleRemove = () => {
    if (isAddingNew) return;
    const updatedTools = tools.filter((_, index) => index !== activeToolIndex);
    setTools(updatedTools);
    setActiveToolIndex(updatedTools.length ? 0 : undefined);
    if (updatedTools.length === 0) {
      setToolType(TOOL_TYPES.CUSTOM);
    }
  };

  const handleCloseModal = (showWarning = false) => {
    if (showWarning && hasUnsavedChanges) {
      setShowSaveConfirmation(true);
      // todo also close modal if user clicks discard
    } else {
      onClose();
    }
  };

  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const renderToolsList = () => {
    const handleToolClick = (index: number) => {
      if (hasUnsavedChanges) {
        setShowSaveConfirmation(true);
      } else {
        setActiveToolIndex(index);
      }
    };

    return (
      <div className="flex flex-col gap-1 pt-2">
        {isAddingNew && (
          <div
            key="new-tool"
            className={cn('cursor-pointer rounded-md bg-secondary-hover p-2')}
          >
            <span className="block text-sm font-semibold text-tertiary">
              New tool
            </span>
          </div>
        )}
        {tools.map((tool, index) => {
          const toolName = getToolDisplayName(tool, modelId);
          return (
            <button
              type="button"
              key={toolName}
              className={cn(
                'w-full rounded-md p-2 hover:bg-secondary-hover',
                index === activeToolIndex && 'bg-secondary-hover'
              )}
              onClick={() => handleToolClick(index)}
            >
              <TruncatedTextWithTooltip
                text={toolName}
                className="block text-left text-sm font-semibold"
                placement="right"
              />
            </button>
          );
        })}
      </div>
    );
  };

  const handleConfirm = () => {
    handleChange(
      isOpenAITool(stagedValue) && 'title' in stagedValue
        ? {
            type: 'function',
            function: convertStructuredPromptSchemaToTool(
              stagedValue as StructuredPromptSchema & {
                strict: boolean | undefined;
              }
            ),
          }
        : stagedValue
    );
  };

  const showBuiltinTools = useMemo(() => {
    return isAddingNew && hasBuiltinTools(modelId);
  }, [isAddingNew, modelId]);

  return (
    <>
      <Modal open={isOpen} onClose={() => handleCloseModal(true)}>
        <ModalDialog
          style={{
            width: '900px',
            height: `${MAX_HEIGHT}px`,
          }}
        >
          <EditorModalHeader
            title="Manage tools"
            description="Tools are functions provided to the model to help it perform specific tasks and return structured outputs."
            link="https://docs.langchain.com/langsmith/prompt-engineering-concepts#tools"
          />
          <div className="grid h-full grid-cols-[1fr,3fr] overflow-hidden pt-4">
            <div className="overflow-hidden border-r border-secondary pr-2">
              <div className="flex w-full flex-row items-center gap-2">
                <span className="grow text-xs font-semibold">Tools</span>
                <Tooltip title="Add tool">
                  <Button
                    color="secondary"
                    variant="outlined"
                    disabled={isAddingNew}
                    onClick={() => {
                      if (hasUnsavedChanges) {
                        setShowSaveConfirmation(true);
                      } else {
                        setToolType(TOOL_TYPES.CUSTOM);
                        setActiveToolIndex(undefined);
                        listScrollRef.current?.scrollTo({
                          top: 0,
                          behavior: 'smooth',
                        });
                      }
                    }}
                  >
                    <PlusIcon className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </div>
              <div
                className="overflow-y-auto"
                style={{ maxHeight: `${MAX_HEIGHT - 120}px` }}
                ref={listScrollRef}
                data-testid="tool-modal-tools-list"
              >
                {renderToolsList()}
              </div>
            </div>
            <div className="flex h-full flex-col gap-2 overflow-y-auto pl-4">
              {showBuiltinTools && (
                <>
                  <div className="flex flex-col gap-2 pb-2 text-lg font-semibold">
                    {isAddingNew ? 'Create tool' : 'Edit tool'}
                  </div>
                  <div className="flex flex-col gap-2 text-sm font-medium">
                    <div className="flex flex-row items-center gap-2">
                      Tool type
                    </div>
                    {toolType === TOOL_TYPES.BUILTIN && (
                      <div className="pt-px">
                        <ToolTypeSelector
                          toolType={toolType}
                          setToolType={setToolType}
                          modelId={modelId}
                          setStagedValue={setStagedValue}
                          isEditingExisting={!isAddingNew}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
              <div className="flex flex-grow flex-col gap-2 overflow-y-auto">
                {isCustomTool ? (
                  <CustomToolEditor
                    title={
                      showBuiltinTools ? (
                        <ToolTypeSelector
                          toolType={toolType}
                          setToolType={setToolType}
                          modelId={modelId}
                          setStagedValue={setStagedValue}
                          isEditingExisting={!isAddingNew}
                        />
                      ) : (
                        <div className="flex flex-col gap-2 pb-2 text-lg font-semibold">
                          {isAddingNew ? 'Create tool' : 'Edit tool'}
                        </div>
                      )
                    }
                    value={stagedValue as StructuredPromptSchema}
                    setValue={
                      setStagedValue as Dispatch<
                        SetStateAction<StructuredPromptSchema>
                      >
                    }
                    onSubmit={handleChange}
                    onRemove={isAddingNew ? undefined : handleRemove}
                    onClose={() => handleCloseModal(false)}
                    saveDisabled={!hasUnsavedChanges}
                    saveTooltipText={getSaveTooltipText()}
                    error={getToolNameError()}
                  />
                ) : (
                  <BuiltinToolEditor
                    modelId={modelId}
                    tool={stagedValue as BuiltinTool}
                    setTool={
                      setStagedValue as Dispatch<SetStateAction<BuiltinTool>>
                    }
                    onClose={() => handleCloseModal(false)}
                    onSave={handleChange}
                    saveDisabled={!hasUnsavedChanges}
                    saveTooltipText={getSaveTooltipText()}
                    setShowRemoveConfirmation={
                      isAddingNew ? undefined : handleRemove
                    }
                    isEditingExisting={!isAddingNew}
                    setIsStreaming={setIsStreaming}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="mt-auto pt-4 text-xs text-quaternary">
            Find tool settings, such as{' '}
            <span className="font-mono">tool_choice</span>, in the prompt
            settings section next to the model selection.
          </div>
        </ModalDialog>
      </Modal>
      {showSaveConfirmation && (
        <SaveConfirmationModal
          isOpen={showSaveConfirmation}
          onClose={() => setShowSaveConfirmation(false)}
          onConfirm={() => {
            handleConfirm();
            setShowSaveConfirmation(false);
          }}
          onDiscard={() => {
            // reset staged value to original value
            setStagedValue(value);
            setShowSaveConfirmation(false);
            // close modal
            onClose();
          }}
          title="Save changes?"
          description="You have unsaved changes. Save before closing?"
        />
      )}
    </>
  );
};

function ToolTypeSelector({
  toolType,
  setToolType,
  modelId,
  setStagedValue,
  isEditingExisting,
}: {
  toolType: ToolType;
  setToolType: (toolType: ToolType) => void;
  modelId: string;
  setStagedValue: Dispatch<
    SetStateAction<OpenAIBuiltinTool | StructuredPromptSchema>
  >;
  isEditingExisting: boolean;
}) {
  return (
    <div className="flex flex-row items-center gap-2">
      <GroupedTabs
        value={toolType}
        onChange={(value) => {
          if (toolType === value) return;
          setToolType(value);
          if (value === TOOL_TYPES.CUSTOM) {
            setStagedValue(
              convertToolToStructuredPromptSchema(
                emptyOpenAITool().function,
                isEditingExisting
              )
            );
          } else {
            setStagedValue(builtinToolsForModelId(modelId)[0].tool);
          }
        }}
        options={[
          {
            value: TOOL_TYPES.CUSTOM,
            icon: <Edit05Icon className="h-4 w-4" />,
            display: 'Custom tool',
          },
          {
            value: TOOL_TYPES.BUILTIN,
            icon: <Tool02Icon className="h-4 w-4" />,
            display: `${
              CHAT_MODEL_MAPPING[modelId.split('/').pop()!].displayName
            } built-in tools`,
          },
        ]}
      />
    </div>
  );
}
